// Global Instructions Rule Applied!
//
// Company States strip (A3, design §8.A3): the compact "what is true right
// now" row on the company Dashboard. Since the pills→cards redesign (owner
// 2026-08-25) this is a thin wrapper over the generic StatesStrip: it binds
// the company services and maps CompanyState rows onto StateItem — the
// public prop surface (accountId / onRefreshReady) is unchanged. The signals
// no longer ride in a footer here — they are their own section on the
// Dashboard, the same as on every other agent surface (owner, 2026-09-06).
import React, { useCallback } from 'react'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  createTaskFromCompanyState,
  fetchCompanyStates,
  overrideCompanyState
} from '../services/internalAgentsService.ts'
import { CompanyState } from '../../../state_manager/data_models/internal_agents_model.ts'
import { StatesStrip } from './StatesStrip.tsx'
import { StateItem } from './StateCard.tsx'

interface CompanyStatesStripProps {
  accountId: string
  /** Hands `load` to the company form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Pre-selects assignees in the create-task form. Never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
}

const toStateItem = (s: CompanyState): StateItem => ({
  id: s.company_states__id,
  stateKey: s.company_state_types__state_key,
  label: s.company_state_types__label,
  kind: s.company_state_types__kind,
  conditionKey: s.company_states__condition_key,
  conditionLabel: s.condition_label,
  conditionRank: s.condition_rank,
  summary: s.company_states__summary,
  status: s.company_states__status,
  startedAt: s.company_states__started_at,
  lastConfirmedAt: s.company_states__last_confirmed_at,
  lastEvidenceAt: s.company_states__last_evidence_at ?? null,
  lastCheckedAt: s.company_states__last_checked_at ?? null,
  expectedEndAt: s.company_states__expected_end_at ?? null,
  expectedEndBasis: s.company_states__expected_end_basis ?? null,
  userSet: s.company_states__user_set,
  conditions: s.conditions ?? [],
  taskId: s.company_states__task_id ?? null,
  taskSubject: s.tasks__subject ?? null,
  taskCompleted: s.tasks__completed ?? null,
  taskDueBy: s.tasks__due_by ?? null
})

export const CompanyStatesStrip: React.FC<CompanyStatesStripProps> = ({
  accountId,
  onRefreshReady = null,
  defaultAssignees = [],
  onTaskCreated
}) => {
  const token = () => getAccessToken(store.getState().auth)

  const fetchStates = useCallback(
    async () => (await fetchCompanyStates(accountId, token())).map(toStateItem),
    [accountId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const overrideState = useCallback(
    async (args: {
      stateKey: string
      action: 'set_condition' | 'edit_summary' | 'close'
      stateId?: string | null
      conditionKey?: string | null
      summary?: string | null
    }) =>
      overrideCompanyState(
        {
          account_id: accountId,
          state_key: args.stateKey,
          action: args.action,
          state_id: args.stateId,
          condition_key: args.conditionKey,
          summary: args.summary
        },
        token()
      ),
    [accountId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const createTask = useCallback(
    async (stateId: string, taskRecord: unknown) =>
      createTaskFromCompanyState(stateId, taskRecord, token()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <StatesStrip
      fetchStates={fetchStates}
      overrideState={overrideState}
      createTaskFromState={createTask}
      defaultAssignees={defaultAssignees}
      onTaskCreated={onTaskCreated}
      subjectNoun='company'
      collapseKey='company_dashboard'
      onRefreshReady={onRefreshReady}
    />
  )
}
